import React from "react";
import { axiosget } from "../../helpers/httprequest";
import { useQuery } from "@tanstack/react-query";
import ReactModal from "react-modal";
import { motion, AnimatePresence } from "framer-motion";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import ApplicationForm from "./ApplicationForm";
import { FiArrowRight, FiFileText, FiX, FiAlertCircle, FiUser, FiBook, FiAward, FiSend } from "react-icons/fi";

const StartNewApplication = ({ v }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const [submitState, setSubmitState] = React.useState({
    canSubmit: false,
    isSubmitting: false,
    onSubmit: null,
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["startApplicationCheck"],
    queryFn: async () => {
      const req = await axiosget(`ms_dashboard`);
      return req?.data;
    },
    enabled: !!showmodal,
  });

  const _close = () => {
    setshowmodal(false);
  };

  let err = 0;
  if (data !== undefined) {
    if (data?.ref < 2) {
      err = 1;
    }
    if (data?.bio < 1) {
      err = 1;
    }
    if (data?.olevel < 1) {
      err = 1;
    }
    if (data?.result < 5) {
      err = 1;
    }
  }

  return (
    <>
      <ReactModal
        isOpen={showmodal}
        onRequestClose={_close}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-2xl w-[80%] max-h-[90vh] overflow-hidden flex flex-col"
          role="document"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 px-6 py-5 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiFileText className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-xl font-semibold text-white">
                  Start New Application
                </h5>
                <p className="text-primary-100 text-sm mt-0.5">
                  Complete your application for admission
                </p>
              </div>
              <button
                onClick={_close}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Close modal"
              >
                <FiX className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 p-6 overflow-y-auto">
            <AnimatePresence mode="wait">
              {isLoading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <FormSkeleton fields={3} />
                </motion.div>
              )}

              {error && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <Alert variant="danger">
                    <AlertDescription>
                      Local server error: {error?.message || error}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {data?.type !== undefined && (
                <motion.div
                  key="api-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <Alert variant="danger">
                    <AlertDescription>{data?.message}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {!isLoading && err === 1 && (
                <motion.div
                  key="validation-errors"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="space-y-4"
                >
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-900 mb-3">
                          Please complete the following requirements:
                        </h4>
                        <ul className="space-y-2">
                          {data?.type === undefined && data?.ref < 2 && (
                            <li className="flex items-start gap-2 text-sm text-amber-800">
                              <FiUser className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>You are required to add 2 referees/guardians</span>
                            </li>
                          )}
                          {data?.type === undefined && data?.bio < 1 && (
                            <li className="flex items-start gap-2 text-sm text-amber-800">
                              <FiUser className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>You need to complete your bio data</span>
                            </li>
                          )}
                          {data?.type === undefined && data?.olevel < 1 && (
                            <li className="flex items-start gap-2 text-sm text-amber-800">
                              <FiBook className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>Please add your O-Level sittings (maximum 2 sittings)</span>
                            </li>
                          )}
                          {data?.type === undefined && data?.result < 5 && (
                            <li className="flex items-start gap-2 text-sm text-amber-800">
                              <FiAward className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>
                                Please add at least 6 O-Level results ({data?.result} currently added)
                              </span>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {data !== undefined && err === 0 && (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <ApplicationForm v={v} onSubmitStateChange={setSubmitState} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer with Submit Button */}
          {submitState.canSubmit && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              className="border-t border-neutral-200 bg-neutral-50 px-6 py-4"
            >
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={submitState.onSubmit}
                loading={submitState.isSubmitting}
                rightIcon={!submitState.isSubmitting && <FiSend />}
              >
                {submitState.isSubmitting ? "Submitting Application..." : "Submit Application"}
              </Button>
            </motion.div>
          )}
        </motion.div>
      </ReactModal>

      <Button
        variant="primary"
        size="md"
        disabled={!v.verified}
        onClick={() => setshowmodal(true)}
        rightIcon={<FiArrowRight />}
        fullWidth
      >
        Continue Application
      </Button>
    </>
  );
};

export default StartNewApplication;
